import { Button, Col, Row, Tag, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons/lib";
import * as React from "react";
import { css, StyleSheet } from "aphrodite";
import { useStoreState } from "../../../../store/hooks";
import { SubState } from "../../../../shared/normalizer";
import { t } from "../../../../translation";
import { BaseOverviewFilter } from "../BaseOverviewFilter";
import { ID } from "../../../../shared/interfaces";

interface IProps {
    /**
     * The SubState for which the title will be generated.
     */
    subState: SubState;
    /**
     * Reload function to reload table data.
     */
    reload: () => void;
    /**
     * Hide the reload button.
     */
    hideReload?: boolean;
    /**
     * Title as string to override the default title.
     */
    title?: string;
    /**
     * If global filters should be enabled.
     */
    globalFilters?: boolean;
    /**
     * Clears all the filters.
     */
    clearFilters: () => void;
    /**
     * Function to update filters.
     */
    setNewFilters: (filters: Record<string, ID[] | null>) => void;
}

const styles = StyleSheet.create({
    titleNoMargin: {
        margin: 0,
    },
    right: {
        float: "right",
    },
});

export const Title: React.FC<IProps> = (props) => {
    const meta = useStoreState((state) => state.normalize.meta[props.subState]);

    /**
     * Removes a filter if it exists in the current filters.
     * @param key: string for which key should be removed.
     */
    const removeFilter = (key: string) => {
        props.setNewFilters({
            [key]: null,
        });
    };

    return (
        <>
            <Row align="middle">
                <Col span={4}>
                    <Typography.Title
                        level={4}
                        className={css(styles.titleNoMargin)}
                    >
                        {props.title || t(`${props.subState}.title`)}
                    </Typography.Title>
                </Col>
                <Col span={16}>
                    {props.globalFilters && (
                        <BaseOverviewFilter
                            subState={props.subState}
                            onFilterChange={props.setNewFilters}
                            filterData={meta.filters}
                        />
                    )}
                </Col>
                <Col span={4}>
                    {!props.hideReload && (
                        <Button
                            className={css(styles.right)}
                            type="default"
                            shape="circle"
                            icon={<ReloadOutlined />}
                            onClick={() => props.reload()}
                            loading={meta.get.loading}
                            data-cy="button-reload"
                        />
                    )}
                </Col>
            </Row>
            <Row align="middle">
                <Col>
                    {Object.entries(meta.filters).map(([key, value]) => (
                        <Tag
                            key={key}
                            closable={true}
                            visible={key !== "page"}
                            onClose={() => removeFilter(key)}
                        >
                            {`${key}=${
                                Array.isArray(value) ? value.join(", ") : value
                            }`}
                        </Tag>
                    ))}
                </Col>
                {Object.keys(meta.filters).length > 1 && (
                    <Col>
                        <Button
                            type="link"
                            onClick={() => props.clearFilters()}
                        >
                            {t("filters.clearFilters")}
                        </Button>
                    </Col>
                )}
            </Row>
        </>
    );
};
